import * as React from "react";

const SELECTOR = [
  "[data-reveal]",
  ".section-gap",
  ".sec-head",
  ".entry",
  ".mini-card",
  ".mini-post",
  ".listing",
].join(",");

// Site-wide scroll reveal. Marks matching elements under <main> with `.reveal`
// (hidden, shifted) and adds `.in` once they scroll into view, staggering
// elements that appear together. A MutationObserver picks up route changes and
// late-loading API content. After the transition the classes are removed so
// they don't fight with the elements' own hover transforms/transitions.
export function useReveal() {
  React.useLayoutEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const io = new IntersectionObserver(
      (entries) => {
        let n = 0;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          io.unobserve(el);
          el.style.setProperty("--i", String(Math.min(n++, 6)));
          el.classList.add("in");
          const done = () => {
            el.classList.remove("reveal", "in");
            el.style.removeProperty("--i");
          };
          el.addEventListener("transitionend", done, { once: true });
          // Fallback if transitionend never fires (element detached, etc).
          setTimeout(done, 1500);
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
    );

    const prepare = (el: Element) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.dataset.revealed || el.closest("[data-no-reveal]")) return;
      el.dataset.revealed = "1";
      el.classList.add("reveal");
      io.observe(el);
    };

    const scan = (root: ParentNode | Element) => {
      if (root instanceof Element && root.matches(SELECTOR)) prepare(root);
      root.querySelectorAll(SELECTOR).forEach(prepare);
    };

    scan(main);
    const mo = new MutationObserver((records) => {
      for (const r of records) r.addedNodes.forEach((node) => node instanceof Element && scan(node));
    });
    mo.observe(main, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
      // Un-prepare anything still waiting to reveal, so a re-run of this
      // effect (StrictMode, remount) observes it again instead of skipping it
      // because of data-revealed and leaving it hidden forever.
      main.querySelectorAll<HTMLElement>("[data-revealed]").forEach((el) => {
        if (el.classList.contains("reveal") && !el.classList.contains("in")) {
          el.classList.remove("reveal");
          delete el.dataset.revealed;
        }
      });
    };
  }, []);
}
